import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../propTypes/userProp';
import HeaderProfilePopup from './HeaderProfilePopup';
import HeaderCartPopup from './HeaderCartPopup';
import ChatService from '../../../../services/chatService';
import translate from '../../../translate';
import Anchor from '../../../Anchor';
import ServicesContext from '../../../../contextProviders/ServicesContext';
import CountryProp from '../../../forms/Address/CountryProp';
import CurrencyProp from '../../../../sections/profile/propTypes/currencyProp';
import AuthOrganizationProp from '../../../../sections/profile/propTypes/authOrganizationProp';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import AuthOrganizationSelector from './AuthOrganizationSelector';
import ShopCartContext from '../../../../contextProviders/ShopCartContext';
import CountrySelector from './CountrySelector';
import MarketInfosContext from '../../../../contextProviders/MarketInfosContext';
import HeaderNotificationsPopup from './notifications/HeaderNotificationsPopup';
import { useBadge } from '../../../../contextProviders/BadgeContextProvider';
import ClassWatcher from '../../../../services/ClassWatcher';
import DemoSelector from './DemoSelector';

/**
 * User header display, with greeting, name and avatar
 */
function HeaderProfile(props) {
  const {
    t,
    profile,
    languages,
    availableCurrencies,
    theme,
    authOrganizations,
    selectedOrganizationId,
    availableCountries,
    bravonLanguages,
  } = props;

  const services = useContext(ServicesContext);
  const chatDisabled =
    services && services.connect && services.connect.enabled === false;
  const cartIsEnabled =
    services && services.marketPlace && services.marketPlace.enabled === true;

  const { shopCart: cart } = useContext(ShopCartContext);
  const { marketInfos } = useContext(MarketInfosContext);
  const { badge } = useBadge();

  const [totalProducts, setTotalProduct] = useState(cart?.length || 0);

  // cart state for HeaderCartPopup component which is initialized as empty array to prevent unnecessary requests
  const [fullCart, setFullCart] = useState([]);

  const fetchCartContent = () => {
    // when user clicks cart badge on header, we pass the cart from context to popup component
    // to trigger fetch request
    if (fullCart.length !== cart.length) {
      setFullCart(cart);
    }
  };

  const handleTotalProducts = numberOfProduct => {
    setTotalProduct(numberOfProduct);
  };

  const openChat = () => ChatService.onOpen();

  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (profile) {
      // To work with metronic's auto hide/show of dropdowns
      // we use an observer to set the state and mount/unmount the notification list
      const toggle = document.querySelector(
        '.notifications-list-dropdown-toggle',
      );
      const watcher = new ClassWatcher(
        toggle,
        'm-dropdown--open',
        () => {
          setShowNotifications(true);
        },
        () => {
          setShowNotifications(false);
        },
      );
      watcher.init();

      return () => {
        watcher.disconnect();
      };
    }
    return () => {};
  }, []);

  return (
    <div
      id="m_header_topbar"
      className="m-topbar m-stack m-stack--ver m-stack--general bravon-topbar"
    >
      <div className="m-stack__item m-topbar__nav-wrapper">
        <ul className="m-topbar__nav m-nav m-nav--inline">
          {profile ? (
            <>
              <DemoSelector />
              {authOrganizations && authOrganizations.length > 1 ? (
                <AuthOrganizationSelector
                  authOrganizations={authOrganizations}
                  selectedOrganizationId={selectedOrganizationId}
                />
              ) : null}
              {availableCurrencies &&
              availableCurrencies.length > 0 &&
              marketInfos.localCurrency ? (
                <CurrencySelector
                  profile={{
                    currency: profile.currency || marketInfos.localCurrency,
                    currencyIsBlocked: profile.currencyIsBlocked,
                  }}
                  currencies={availableCurrencies}
                />
              ) : null}
              {availableCountries &&
              availableCountries.length > 0 &&
              marketInfos.localCountry ? (
                <CountrySelector
                  profile={{
                    shippingCountry:
                      profile.shippingCountry || marketInfos.localCountry,
                  }}
                  countries={availableCountries}
                />
              ) : null}
              {languages &&
              languages.length > 0 &&
              marketInfos.localLanguage ? (
                <LanguageSelector
                  profile={profile}
                  languages={languages}
                  bravonLanguages={bravonLanguages}
                />
              ) : null}
              {cartIsEnabled && (
                <li
                  className={
                    'm-nav__item m-topbar__cart ' +
                    'm-topbar__cart--img m-dropdown m-dropdown--medium ' +
                    'm-dropdown--arrow m-dropdown--header-bg-fill m-dropdown--align-right ' +
                    'm-dropdown--mobile-full-width m-dropdown--skin-light'
                  }
                  m-dropdown-toggle="click"
                >
                  <a
                    href="#"
                    className="m-nav__link m-dropdown__toggle"
                    onClick={() => fetchCartContent()}
                  >
                    <span
                      className="m-nav__link-badge m-badge m-badge--dot m-badge--dot-small m-badge--danger"
                      id="badgeCart"
                      style={{ display: 'none' }}
                    />
                    <span className="m-nav__link-icon m-nav__link-icon-alt">
                      <span className="m-nav__link-icon-wrapper link-icon-with-badge">
                        <div className="link-icon-badge">{totalProducts}</div>
                        <i className="la la-shopping-cart" />
                      </span>
                    </span>
                  </a>
                  <HeaderCartPopup
                    t={t}
                    cart={fullCart}
                    onTotalProducts={handleTotalProducts}
                  />
                </li>
              )}
              <li
                className={
                  'm-nav__item m-topbar__user-profile ' +
                  'm-topbar__user-profile--img m-dropdown m-dropdown--medium ' +
                  'm-dropdown--arrow m-dropdown--header-bg-fill m-dropdown--align-right ' +
                  'm-dropdown--mobile-full-width m-dropdown--skin-light'
                }
                m-dropdown-toggle="click"
              >
                <a href="#" className="m-nav__link m-dropdown__toggle">
                  <span className="m-topbar__userpic">
                    <img
                      style={{
                        width: '41px',
                        height: '41px',
                        objectFit: 'cover',
                      }}
                      src={profile.avatar}
                      className="m--img-rounded m--img-centered m--marginless"
                      alt="logged user"
                    />
                  </span>
                </a>
                <HeaderProfilePopup profile={profile} theme={theme} />
              </li>
              <li
                className={
                  'notifications-list-dropdown-toggle m-nav__item m-dropdown m-dropdown--huge ' +
                  'm-dropdown--arrow m-dropdown--header-bg-fill m-dropdown--align-right ' +
                  'm-dropdown--mobile-full-width m-dropdown--skin-light'
                }
                m-dropdown-toggle="click"
              >
                <a href="#" className="m-nav__link m-dropdown__toggle">
                  <span className="m-nav__link-icon m-nav__link-icon-alt notifications-link-icon">
                    <span className="m-nav__link-icon-wrapper link-icon-with-badge">
                      <div
                        className="link-icon-badge"
                        id="badgeNotification"
                        style={{
                          display: badge?.notifications ? 'block' : 'none',
                        }}
                      >
                        {badge?.notifications}
                      </div>
                      <i className="flaticon-alarm" />
                    </span>
                  </span>
                </a>
                <HeaderNotificationsPopup show={showNotifications} />
              </li>
              {!chatDisabled && (
                <li id="m_quick_sidebar_toggle" className="m-nav__item">
                  <button
                    className="m-nav__link m-dropdown__toggle"
                    onClick={openChat}
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                    }}
                  >
                    <span
                      className="m-nav__link-badge m-badge m-badge--dot m-badge--dot-small m-badge--danger"
                      id="badgeChat"
                      style={{ display: 'none' }}
                    />
                    <span className="m-nav__link-icon m-nav__link-icon-alt">
                      <span className="m-nav__link-icon-wrapper">
                        <i className="flaticon-chat-1" />
                      </span>
                    </span>
                  </button>
                </li>
              )}
            </>
          ) : (
            <>
              {availableCurrencies &&
              availableCurrencies.length > 0 &&
              marketInfos.localCurrency ? (
                <CurrencySelector
                  profile={{ currency: marketInfos.localCurrency }}
                  currencies={availableCurrencies}
                />
              ) : null}
              {availableCountries &&
              availableCountries.length > 0 &&
              marketInfos.localCountry ? (
                <CountrySelector
                  profile={{ shippingCountry: marketInfos.localCountry }}
                  countries={availableCountries}
                />
              ) : null}
              {languages && languages.length > 0 ? (
                <LanguageSelector
                  profile={{ language: marketInfos.localLanguage || 'en' }}
                  languages={languages}
                  bravonLanguages={bravonLanguages}
                />
              ) : null}
              {cartIsEnabled && (
                <li
                  className={
                    'm-nav__item m-topbar__cart ' +
                    'm-topbar__cart--img m-dropdown m-dropdown--medium ' +
                    'm-dropdown--arrow m-dropdown--header-bg-fill m-dropdown--align-right ' +
                    'm-dropdown--mobile-full-width m-dropdown--skin-light'
                  }
                  m-dropdown-toggle="click"
                >
                  <a
                    href="#"
                    className="m-nav__link m-dropdown__toggle"
                    onClick={() => fetchCartContent()}
                  >
                    <span
                      className="m-nav__link-badge m-badge m-badge--dot m-badge--dot-small m-badge--danger"
                      id="badgeCart"
                      style={{ display: 'none' }}
                    />
                    <span className="m-nav__link-icon m-nav__link-icon-alt">
                      <span className="m-nav__link-icon-wrapper link-icon-with-badge">
                        <div className="link-icon-badge">{totalProducts}</div>
                        <i className="la la-shopping-cart" />
                      </span>
                    </span>
                  </a>
                  <HeaderCartPopup
                    t={t}
                    cart={fullCart}
                    onTotalProducts={handleTotalProducts}
                  />
                </li>
              )}
              <li className="m-nav__item">
                <Anchor href="/signin" className="m-nav__link" blockLink>
                  <span className="m-nav__link-text">{t('login')}</span>
                </Anchor>
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}

HeaderProfile.propTypes = {
  t: PropTypes.func.isRequired,
  /** Member to display */
  profile: PropTypes.shape(UserProp.propType),
  /** metronic theme being used */
  theme: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  availableCurrencies: PropTypes.arrayOf(
    PropTypes.shape(CurrencyProp.propType),
  ),
  authOrganizations: PropTypes.arrayOf(
    PropTypes.shape(AuthOrganizationProp.propType),
  ),
  selectedOrganizationId: PropTypes.string,
  availableCountries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  bravonLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
};

HeaderProfile.defaultProps = {
  profile: null,
  languages: [],
  availableCurrencies: [],
  authOrganizations: [],
  selectedOrganizationId: null,
};

export default translate(HeaderProfile);
